import { Flow, FlowFilters, FlowType, CommandStep } from './flow';

type DbCommandStep =
  | {
      kind?: 'command';
      type?: 'command';
      command: string;
      alias?: string;
      sleep?: number;
      timeout?: number;
    }
  | { kind?: 'sleep'; type?: 'sleep'; duration: number }
  | { kind?: 'event'; type?: 'event'; event: string }
  | { kind?: 'video'; type?: 'video'; options?: Record<string, any> }
  | {
      kind?: 'mock-command';
      type?: 'mock-command';
      substitute?: string;
      substituteName?: string;
    };

export type DbTvCommand = {
  _id: string;
  key?: string;
  name?: string;
  type?: FlowType;
  description?: string;
  filters?: {
    adSlots?: string[];
    osvs?: string[];
    make?: string[];
    adTypes?: string[];
    destTypes?: string[];
    countries?: string[];
  };
  commands?: DbCommandStep[];
  tags?: string[];
  isDefault?: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
};

function formatDate(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function dbToUiFlow(doc: DbTvCommand): Flow {
  const filters: FlowFilters | undefined =
    doc.type === 'adslot' || doc.type === 'pre'
      ? {
          adSlots: doc.type === 'adslot' ? doc.filters?.adSlots ?? [] : [],
          osVersions: doc.filters?.osvs ?? [],
          makes: doc.type === 'adslot' ? doc.filters?.make ?? [] : [],
          adTypes: doc.type === 'adslot' ? doc.filters?.adTypes ?? [] : [],
          destTypes: doc.type === 'adslot' ? doc.filters?.destTypes ?? [] : [],
          countries: doc.type === 'adslot' ? doc.filters?.countries ?? [] : [],
        }
      : undefined;

  const commands: CommandStep[] = (doc.commands ?? []).map((c, idx) => {
    const kind = c.kind || c.type || 'command';

    if (kind === 'sleep') {
      return {
        id: `${doc._id}-sleep-${idx}`,
        type: 'sleep',
        duration: (c as any).duration,
      };
    }

    if (kind === 'event') {
      return {
        id: `${doc._id}-event-${idx}`,
        type: 'event',
        event: (c as any).event,
        label: (c as any).event || 'Event',
      };
    }

    if (kind === 'video') {
      return {
        id: `${doc._id}-video-${idx}`,
        type: 'video',
        options: (c as any).options ?? {},
        label: `Video ${(c as any).options?.type || ''}`.trim(),
      };
    }

    if (kind === 'mock-command') {
      return {
        id: `${doc._id}-mock-${idx}`,
        type: 'mock-command',
        substitute: (c as any).substitute || (c as any).substituteName || '',
        label: (c as any).substitute || (c as any).substituteName || 'Mock command',
      };
    }

    return {
      id: `${doc._id}-cmd-${idx}`,
      type: 'command',
      command: (c as any).command,
      label: (c as any).alias || (c as any).command,
      sleep: (c as any).sleep,
      timeout: (c as any).timeout,
    };
  });

  return {
    id: doc._id,
    key: doc.key,
    name: doc.name ?? '',
    type: (doc.type ?? 'adslot') as FlowType,
    description: doc.description ?? '',
    isDefault: doc.isDefault ?? false,
    commands,
    filters,
    tags: doc.tags ?? [],
    updatedAt: formatDate(doc.updatedAt),
    updatedBy: doc.updatedBy ?? 'system',
  };
}

export function uiToDbPayload(flow: Flow) {
  const commands: DbCommandStep[] = flow.commands.map(c => {
    if (c.type === 'sleep') {
      return { kind: 'sleep', duration: c.duration ?? 1 };
    }

    if (c.type === 'event') {
      return { kind: 'event', event: c.event ?? c.label ?? '' };
    }

    if (c.type === 'video') {
      return { kind: 'video', options: c.options ?? {} };
    }

    if (c.type === 'mock-command') {
      return { kind: 'mock-command', substitute: c.substitute ?? c.label ?? '' };
    }

    const alias = c.label && c.label !== c.command ? c.label : undefined;

    return {
      kind: 'command',
      command: c.command ?? '',
      ...(alias ? { alias } : {}),
      ...(typeof c.sleep === 'number' ? { sleep: c.sleep } : {}),
      ...(typeof c.timeout === 'number' ? { timeout: c.timeout } : {}),
    };
  });

  const payload: any = {
    name: flow.name,
    key: flow.key,
    type: flow.type,
    description: flow.description ?? '',
    commands,
    tags: flow.tags ?? [],
    isDefault: flow.isDefault ?? false,
  };

  if (flow.type === 'adslot' || flow.type === 'pre') {
    payload.filters = {
      adSlots: flow.type === 'adslot' ? flow.filters?.adSlots ?? [] : [],
      osvs: flow.filters?.osVersions ?? [],
      make: flow.type === 'adslot' ? flow.filters?.makes ?? [] : [],
      adTypes: flow.type === 'adslot' ? flow.filters?.adTypes ?? [] : [],
      destTypes: flow.type === 'adslot' ? flow.filters?.destTypes ?? [] : [],
      countries: flow.type === 'adslot' ? flow.filters?.countries ?? [] : [],
    };
  }

  return payload;
}

export function isMongoObjectId(id: string) {
  return /^[a-f0-9]{24}$/i.test(id);
}
